import React from "react";
import { RefreshCw, ExternalLink, BarChart3, Clock, Brain } from "lucide-react";
import { Button } from "./ui/button";

const fmt = (n) => {
  if (n === null || n === undefined) return "—";
  if (n >= 1e6) return `${(n / 1e6).toFixed(1).replace(".", ",")} M`;
  if (n >= 1e3) return `${Math.round(n / 1e3)} K`;
  return String(n);
};

const Bar = ({ g, star, unit, unitOne }) => (
  <div className="flex items-center gap-2 text-xs min-w-0">
    <span className="w-24 sm:w-32 shrink-0 truncate text-slate-300">{g.label}{star ? " ★" : ""}</span>
    <div className="flex-1 h-2 rounded-full bg-slate-800 overflow-hidden">
      <div className={`h-full ${g.mult >= 1 ? "bg-emerald-500" : "bg-amber-500"}`} style={{ width: `${Math.round(((g.mult - 0.6) / 1.0) * 100)}%` }} />
    </div>
    <span className={`w-12 text-right font-mono ${g.mult >= 1 ? "text-emerald-300" : "text-amber-300"}`}>×{String(g.mult).replace(".", ",")}</span>
    <span className="hidden sm:inline w-28 text-right text-[10px] text-slate-500 font-mono">{g.n} {g.n === 1 ? unitOne : unit} · {fmt(g.avg_views)}</span>
  </div>
);

/**
 * Panel «Qué funciona en tu canal», común al Radar Viral, Dailymotion e Instagram (el de Facebook va aparte).
 * learn = {measured, tracked, private, groups: {clave: {label, n, mult, avg_views}}, hours, best, favs}
 */
export const LearningPanel = ({
  testId,
  learn,
  enabled,
  onToggle,
  onRefresh,
  refreshing,
  saving,
  lastStats,
  lastError,
  unit = "vídeos",
  unitOne = "vídeo",
  statsNote,
  onText,
  offText,
  privateText,
  favText,
  groupTitle = "Categorías (multiplicador de prioridad)",
  emptyText,
  extra,
  accent = "amber"
}) => {
  const data = learn || { measured: 0, tracked: 0, private: 0, groups: {}, hours: [], best: [], favs: [] };
  const groups = Object.entries(data.groups || {}).sort((a, b) => b[1].mult - a[1].mult);
  const favs = data.favs || [];
  const chip = accent === "cyan"
    ? "bg-cyan-500/15 text-cyan-200 border-cyan-500/30"
    : accent === "pink"
      ? "bg-pink-500/15 text-pink-200 border-pink-500/30"
      : accent === "violet"
        ? "bg-violet-500/15 text-violet-200 border-violet-500/30"
        : "bg-amber-500/15 text-amber-200 border-amber-500/30";
  const icon = accent === "cyan" ? "text-cyan-400" : accent === "pink" ? "text-pink-400"
    : accent === "violet" ? "text-violet-400" : "text-amber-400";

  return (
    <div data-testid={testId} className="bg-[#12141D] border border-white/10 rounded-2xl p-6 space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="min-w-0">
          <h4 className="text-sm font-black text-white flex items-center gap-1.5"><Brain className={`w-4 h-4 ${icon}`} /> Qué funciona en tu canal</h4>
          <p className="text-[11px] text-slate-500 mt-0.5">
            {data.measured} de {data.tracked} {unit} medidos · Estadísticas: {lastStats ? new Date(lastStats).toLocaleString("es-ES") : "nunca"}
            {statsNote ? ` (${statsNote})` : ""}
          </p>
          {lastError && <p className="text-[11px] text-red-400 mt-0.5 break-words">{lastError}</p>}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button
            data-testid={`${testId}-refresh`}
            onClick={onRefresh}
            disabled={refreshing || !enabled}
            className="bg-slate-800 hover:bg-slate-700 text-white text-xs h-8 px-3"
          >
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${refreshing ? "animate-spin" : ""}`} /> Leer estadísticas
          </Button>
          <button
            data-testid={`${testId}-toggle`}
            role="switch"
            aria-checked={enabled}
            onClick={onToggle}
            disabled={saving}
            className={`w-10 h-6 rounded-full relative transition-colors shrink-0 ${enabled ? "bg-emerald-500" : "bg-slate-700"}`}
            title={enabled ? "Apagar el aprendizaje" : "Encender el aprendizaje"}
          >
            <span className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${enabled ? "left-5" : "left-1"}`} />
          </button>
        </div>
      </div>
      <p className="text-[11px] text-slate-400">{enabled ? onText : offText}</p>
      {data.private > 0 && privateText && <p className="text-[11px] text-amber-300">{privateText(data.private)}</p>}
      {data.measured === 0 ? (
        <p className="text-xs text-slate-500">
          {emptyText || `Todavía no hay datos. Cuando tus ${unit} lleven más de 24 h publicados, aquí verás qué funciona mejor, a qué horas y cuáles son tus mejores ${unit}. Mientras tanto, todo tiene la misma prioridad.`}
        </p>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 space-y-1.5 min-w-0">
            <span className="text-[11px] uppercase font-bold text-slate-400 block">{groupTitle}</span>
            {groups.map(([key, g]) => (
              <Bar key={key} g={g} star={favs.includes(key)} unit={unit} unitOne={unitOne} />
            ))}
            {favs.length > 0 && favText && <span className="text-[10px] text-slate-500 block pt-1">★ = {favText}</span>}
            {extra && Object.keys(extra.groups || {}).length > 0 && (
              <div className="pt-3 space-y-1.5">
                <span className="text-[11px] uppercase font-bold text-slate-400 block">{extra.title}</span>
                {Object.entries(extra.groups).sort((a, b) => b[1].mult - a[1].mult).map(([key, g]) => (
                  <Bar key={key} g={g} unit={extra.unit || unit} unitOne={extra.unitOne || unitOne} />
                ))}
              </div>
            )}
          </div>
          <div className="space-y-3 min-w-0">
            <div>
              <span className="text-[11px] uppercase font-bold text-slate-400 flex items-center gap-1"><Clock className="w-3 h-3" /> Mejores horas (España)</span>
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {(data.hours || []).map((h) => (
                  <span key={h.hour} className={`text-[11px] font-mono px-2 py-0.5 rounded border ${chip}`}>
                    {String(h.hour).padStart(2, "0")}:00 · {fmt(h.avg_views)}
                  </span>
                ))}
              </div>
            </div>
            <div>
              <span className="text-[11px] uppercase font-bold text-slate-400 flex items-center gap-1"><BarChart3 className="w-3 h-3" /> Mejores {unit}</span>
              <ul className="mt-1 space-y-1">
                {(data.best || []).map((b) => (
                  <li key={b.id} className="text-[11px] text-slate-300 flex items-center gap-1.5 min-w-0">
                    <span className="font-mono text-emerald-300 shrink-0">{fmt(b.views)}</span>
                    {b.url ? (
                      <a href={b.url} target="_blank" rel="noopener noreferrer" className="truncate hover:text-white flex items-center gap-1 min-w-0">
                        <span className="truncate">{b.title}</span> <ExternalLink className="w-3 h-3 shrink-0" />
                      </a>
                    ) : <span className="truncate">{b.title}</span>}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
